import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Search, User, Users, X, Zap } from 'lucide-react';

/** Multi-select stores and provider groups. A provider contributes its stores on the server. */
export function UserSelector({ users, usersLoading, activeUsersOnly, setActiveUsersOnly,
  selectedUserIds = [], selectedProviderIds = [], onChange, onSearch }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [remembered, setRemembered] = useState({});
  const [up, setUp] = useState(false);
  const [maxHeight, setMaxHeight] = useState(320);
  const root = useRef(null);
  const trigger = useRef(null);
  const selectedUsers = selectedUserIds.map(Number);
  const selectedProviders = selectedProviderIds.map(Number);
  const selectedCount = selectedUsers.length + selectedProviders.length;

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => onSearch?.(search), 350);
    return () => clearTimeout(timer);
  }, [open, search, onSearch]);
  useEffect(() => {
    if (!open) return;
    const close = event => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  useEffect(() => {
    if (!open || !trigger.current) return;
    const rect = trigger.current.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom;
    const placeUp = below < 320 && rect.top > below;
    setUp(placeUp);
    setMaxHeight(Math.max(80, Math.min(placeUp ? rect.top - 8 : below - 8, 384)));
  }, [open]);

  const label = (type, id) => {
    const item = (users || []).find(row => row.type === type && Number(row.id) === id) || remembered[`${type}:${id}`];
    return item ? (type === 'provider' ? item.name : item.username) : `${type === 'provider' ? 'Proveedor' : 'Tienda'} #${id}`;
  };
  const toggle = item => {
    const id = Number(item.id);
    const isProvider = item.type === 'provider';
    const current = isProvider ? selectedProviders : selectedUsers;
    if (!current.includes(id) && selectedCount >= 100) return;
    setRemembered(previous => ({ ...previous, [`${item.type}:${id}`]: item }));
    const next = current.includes(id) ? current.filter(value => value !== id) : [...current, id];
    onChange(isProvider ? { user_ids: selectedUsers, provider_ids: next }
      : { user_ids: next, provider_ids: selectedProviders });
  };
  const remove = (type, id) => onChange(type === 'provider'
    ? { user_ids: selectedUsers, provider_ids: selectedProviders.filter(value => value !== id) }
    : { user_ids: selectedUsers.filter(value => value !== id), provider_ids: selectedProviders });
  const visible = (users || []).filter(item => {
    const value = `${item.type === 'provider' ? item.name : item.username} ${item.email || ''} ${item.id}`.toLowerCase();
    return value.includes(search.toLowerCase());
  });

  return <div ref={root} className="relative min-w-0">
    <div className="mb-2 flex items-center gap-2">
      <button type="button" onClick={() => setActiveUsersOnly(previous => !previous)}
        className={`flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-semibold ${activeUsersOnly
          ? 'border-brand-green/30 bg-brand-green/10 text-brand-green' : 'border-gray-200 bg-gray-100 text-gray-500'}`}>
        <Zap size={11}/> Solo activos
      </button>
      <span className="text-xs text-gray-400">{(users || []).length} opciones</span>
    </div>
    <button ref={trigger} type="button" aria-label="Seleccionar tiendas y proveedores" aria-expanded={open}
      onClick={() => setOpen(value => !value)}
      className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm hover:border-gray-300">
      <Users size={15} className="shrink-0 text-gray-400"/>
      <span className={`min-w-0 flex-1 truncate ${selectedCount ? 'font-semibold text-gray-700' : 'text-gray-400'}`}>
        {selectedCount ? `${selectedUsers.length} tienda${selectedUsers.length === 1 ? '' : 's'} · ${selectedProviders.length} proveedor${selectedProviders.length === 1 ? '' : 'es'}`
          : 'Todas las tiendas y proveedores'}
      </span>
      <ChevronDown size={15} className={`shrink-0 text-gray-400 ${open ? 'rotate-180' : ''}`}/>
    </button>
    {selectedCount > 0 && <div className="mt-2 flex flex-wrap gap-1">
      {selectedUsers.map(id => <button key={`user:${id}`} type="button" aria-label={`Quitar tienda ${label('user', id)}`}
        onClick={() => remove('user', id)} className="flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-700">
        {label('user', id)} <X size={12}/>
      </button>)}
      {selectedProviders.map(id => <button key={`provider:${id}`} type="button" aria-label={`Quitar proveedor ${label('provider', id)}`}
        onClick={() => remove('provider', id)} className="flex items-center gap-1 rounded-full bg-purple-50 px-2 py-1 text-xs text-purple-700">
        {label('provider', id)} <X size={12}/>
      </button>)}
    </div>}
    {open && <div className={`absolute left-0 z-50 flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl ${up ? 'bottom-full mb-1' : 'top-full mt-1'}`}
      style={{ width: 'min(36rem, calc(100vw - 2rem))', maxHeight }}>
      <div className="relative border-b border-gray-100 p-2">
        <Search size={14} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"/>
        <input autoFocus type="search" aria-label="Buscar tienda o proveedor" value={search}
          onChange={event => setSearch(event.target.value)} placeholder="Buscar tienda, proveedor o ID"
          className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-8 pr-3 text-sm focus:border-brand-primary focus:outline-none"/>
      </div>
      <div className="min-h-0 overflow-y-auto">
        {usersLoading ? <p className="p-3 text-sm text-gray-500">Cargando…</p>
          : visible.length === 0 ? <p className="p-3 text-sm text-gray-500">Sin resultados</p>
          : visible.map(item => {
            const checked = item.type === 'provider' ? selectedProviders.includes(Number(item.id)) : selectedUsers.includes(Number(item.id));
            return <button key={`${item.type}:${item.id}`} type="button" role="checkbox" aria-checked={checked}
              aria-label={`${item.type === 'provider' ? 'Proveedor' : 'Tienda'} ${item.type === 'provider' ? item.name : item.username}`}
              onClick={() => toggle(item)} className={`flex w-full items-center gap-2 border-b border-gray-50 px-3 py-2 text-left text-sm hover:bg-gray-50 ${checked ? 'bg-brand-primary/5' : ''}`}>
              {item.type === 'provider' ? <Users size={16} className="shrink-0 text-purple-500"/> : <User size={16} className="shrink-0 text-gray-400"/>}
              <span className="min-w-0 flex-1"><span className="block truncate font-semibold text-gray-700">{item.type === 'provider' ? item.name : item.username} #{item.id}</span>
                <span className="block truncate text-xs text-gray-500">{item.email}{item.type === 'provider' ? ` · ${item.store_count} tiendas` : ''}</span></span>
              <span className="text-xs text-gray-500">{item.package_count} paq.</span>
              <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${checked ? 'border-brand-primary bg-brand-primary text-white' : 'border-gray-300'}`}>
                {checked && <Check size={12}/>}
              </span>
            </button>;
          })}
      </div>
      {selectedCount > 0 && <button type="button" onClick={() => onChange({ user_ids: [], provider_ids: [] })}
        className="border-t border-gray-100 p-2 text-sm font-semibold text-gray-600 hover:text-brand-primary">Limpiar selección</button>}
    </div>}
  </div>;
}
